import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  AvatarGroup,
  Tooltip
} from '@mui/material';
import {
  MoreVert,
  FolderOpen,
  Assignment,
  Timeline,
  BugReport,
  PlayArrow,
  Pause,
  Group
} from '@mui/icons-material';
import { Project, ProjectMetrics } from '../../types';
import { useNavigate } from 'react-router-dom';

interface ProjectCardProps {
  project: Project;
  metrics?: ProjectMetrics;
  onEdit?: (project: Project) => void;
  onArchive?: (project: Project) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ 
  project, 
  metrics,
  onEdit,
  onArchive 
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProjectClick = () => {
    navigate(`/projects/${project.id}`);
  };

  // Removed direct board navigation from project card to encourage sprint-specific boards

  const getMethodologyIcon = () => {
    switch (project.methodology) {
      case 'scrum':
        return <Timeline />;
      case 'kanban':
        return <Assignment />;
      default:
        return <FolderOpen />;
    }
  };

  const getMethodologyColor = () => {
    switch (project.methodology) {
      case 'scrum':
        return 'primary';
      case 'kanban':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
      onClick={handleProjectClick}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {project.avatar ? (
              <Avatar src={project.avatar} sx={{ width: 40, height: 40 }} />
            ) : (
              <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                {getMethodologyIcon()}
              </Avatar>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }} noWrap>
                {project.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {project.key}
              </Typography>
            </Box>
          </Box>

          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem onClick={() => { handleProjectClick(); handleMenuClose(); }}>
              <FolderOpen sx={{ mr: 1 }} />
              View Project Details
            </MenuItem>
            <MenuItem onClick={() => { navigate(`/project-assignments?projectId=${project.id}`); handleMenuClose(); }}>
              <Group sx={{ mr: 1 }} />
              Assign Employees
            </MenuItem>
            {onEdit && (
              <MenuItem onClick={() => { onEdit(project); handleMenuClose(); }}>
                Edit Project
              </MenuItem>
            )}
            {onArchive && (
              <MenuItem onClick={() => { onArchive(project); handleMenuClose(); }}>
                Archive Project
              </MenuItem>
            )}
          </Menu>
        </Box>

        {/* Description */}
        {project.description && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2, 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {project.description}
          </Typography>
        )}

        {/* Methodology Chip */}
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={getMethodologyIcon()}
            label={project.methodology.toUpperCase()}
            color={getMethodologyColor() as any}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Status chips */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={project.isActive ? 'Active' : 'Inactive'}
            color={project.isActive ? 'success' : 'default'}
            size="small"
          />
          {metrics && (
            <Chip
              label={`Progress ${metrics.completionRate}%`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {/* Enhanced Metrics */}
        {metrics && (
          <Box sx={{ mb: 2 }}>
            {/* Progress Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {metrics.completionRate}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={metrics.completionRate} 
              sx={{ height: 6, borderRadius: 3, mb: 2 }}
            />

            {/* Story Points Progress */}
            {metrics.totalStoryPoints > 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Story Points
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {metrics.storyPointsCompletionRate}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={metrics.storyPointsCompletionRate} 
                  sx={{ height: 4, borderRadius: 2, mb: 2 }}
                  color="secondary"
                />
              </>
            )}
            
            {/* Key Metrics Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {metrics.totalIssues}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Issues
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="success.main">
                  {metrics.completedIssues}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Completed
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="warning.main">
                  {metrics.activeIssues}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="info.main">
                  {metrics.totalSprints}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Sprints
                </Typography>
              </Box>
            </Box>

            {/* Additional Metrics Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              {metrics.overdue > 0 && (
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="h6" color="error.main">
                    {metrics.overdue}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Overdue
                  </Typography>
                </Box>
              )}
              {metrics.velocity > 0 && (
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="h6" color="primary.main">
                    {Math.round(metrics.velocity)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Velocity
                  </Typography>
                </Box>
              )}
              {metrics.healthScore !== undefined && (
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography 
                    variant="h6" 
                    color={
                      metrics.healthScore >= 80 ? 'success.main' :
                      metrics.healthScore >= 60 ? 'warning.main' : 'error.main'
                    }
                  >
                    {metrics.healthScore}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Health
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Epic Progress */}
            {metrics.totalEpics > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Epics: {metrics.completedEpics}/{metrics.totalEpics}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {metrics.epicCompletionRate}% complete
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Team Members */}
        {project.teams && project.teams[0]?.members && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Team
            </Typography>
            <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
              {project.teams[0].members.filter(m => m.isActive).map((member) => (
                <Tooltip key={member.id} title={member.user?.fullName}>
                  <Avatar>
                    {member.user?.firstName?.[0]}{member.user?.lastName?.[0]}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          </Box>
        )}

        {/* Active Sprint Indicator */}
        {project.sprints?.some(s => s.isActive) && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
            <PlayArrow sx={{ fontSize: 16, mr: 1, color: 'success.dark' }} />
            <Typography variant="caption" color="success.dark" fontWeight="medium">
              Active Sprint Running
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectCard;